import { useCallback, useRef } from 'react';

import { message } from 'antd';

type UseSearchLengthValidationProps = {
  maxLength: number;
  warningMessage: string;
  debounceMs?: number;
  /**
   * SearchInput: false (브라우저의 maxLength가 입력을 차단, === 체크)
   * TreeSelect: true (maxLength prop이 동작 안 함, > 체크 + truncate)
   */
  shouldTruncate?: boolean;
};

export const useSearchLengthValidation = ({
  maxLength,
  warningMessage,
  debounceMs = 1000,
  shouldTruncate = false,
}: UseSearchLengthValidationProps) => {
  const lastWarningTimeRef = useRef<number>(0);
  const isComposingRef = useRef<boolean>(false);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false;
  }, []);

  const showWarning = useCallback(() => {
    if (!isComposingRef.current) {
      const now = Date.now();
      if (now - lastWarningTimeRef.current > debounceMs) {
        message.warning(warningMessage);
        lastWarningTimeRef.current = now;
      }
    }
  }, [warningMessage, debounceMs]);

  const validateLength = useCallback(
    (value: string): string => {
      if (shouldTruncate) {
        // TreeSelect: maxLength prop이 동작하지 않으므로 수동 truncate
        if (value.length > maxLength) {
          showWarning();
          return value.slice(0, maxLength);
        }
      } else {
        // SearchInput: 브라우저의 maxLength가 입력을 차단
        // 정확히 maxLength에 도달했을 때 메시지 표시
        if (value.length === maxLength) {
          showWarning();
        }
      }
      return value;
    },
    [maxLength, shouldTruncate, showWarning]
  );

  return {
    validateLength,
    handleCompositionStart,
    handleCompositionEnd,
  };
};
